// Cammy — CamCom mascot. Builds a THREE.Group of named meshes + tick(t) animator.
export function buildCammy(THREE, { logoUrl = 'uploads/camcom-logo.png' } = {}) {
  const M = {
    black: new THREE.MeshStandardMaterial({ color: 0x24272b, roughness: 0.28, metalness: 0.12 }),
    red: new THREE.MeshStandardMaterial({ color: 0xd7262c, roughness: 0.34, metalness: 0.05 }),
    white: new THREE.MeshStandardMaterial({ color: 0xf3f1ec, roughness: 0.3, metalness: 0.0 }),
    dark: new THREE.MeshStandardMaterial({ color: 0x101114, roughness: 0.14, metalness: 0.2 }),
    lens: new THREE.MeshStandardMaterial({ color: 0x131417, roughness: 0.1, metalness: 0.25, emissive: 0x000000 }),
    magGlass: new THREE.MeshStandardMaterial({ color: 0xcfe4e8, roughness: 0.08, transparent: true, opacity: 0.35 }),
    beam: new THREE.MeshBasicMaterial({ color: 0xff3b30, transparent: true, opacity: 0, depthWrite: false, side: THREE.DoubleSide }),
  };
  M.black.name = 'gloss_black'; M.red.name = 'camcom_red'; M.white.name = 'soft_white';
  M.dark.name = 'dark_glass'; M.lens.name = 'chest_lens'; M.magGlass.name = 'magnifier_glass'; M.beam.name = 'scan_beam';

  const mesh = (name, geo, mat, parent, pos, rot, scl) => {
    const m = new THREE.Mesh(geo, mat);
    m.name = name;
    if (pos) m.position.set(...pos);
    if (rot) m.rotation.set(...rot);
    if (scl) m.scale.set(...scl);
    parent.add(m);
    return m;
  };

  const root = new THREE.Group(); root.name = 'Cammy';

  // ---- legs + shoes (hip pivots so pages can drive run cycles) ----
  const hips = {};
  for (const s of [-1, 1]) {
    const side = s < 0 ? 'L' : 'R';
    const hip = new THREE.Group(); hip.name = 'hip_' + side; hip.position.set(s * 0.096, 0.3, 0); root.add(hip);
    hips[side] = hip;
    mesh('shoe_' + side, new THREE.SphereGeometry(1, 32, 24), M.red, hip, [s * 0.004, -0.225, 0.025], null, [0.115, 0.075, 0.155]);
    mesh('leg_' + side, new THREE.CylinderGeometry(0.047, 0.052, 0.24, 20), M.black, hip, [0, -0.09, 0]);
    mesh('sock_' + side, new THREE.CylinderGeometry(0.055, 0.055, 0.045, 20), M.white, hip, [0, -0.165, 0]);
  }

  // ---- body group (bobs as one) ----
  const body = new THREE.Group(); body.name = 'body'; root.add(body);
  mesh('torso', new THREE.CapsuleGeometry(0.175, 0.22, 8, 32), M.white, body, [0, 0.44, 0], null, [1.04, 1, 0.96]);
  // chest camera lens
  mesh('chest_ring', new THREE.CylinderGeometry(0.072, 0.072, 0.035, 32), M.red, body, [0, 0.565, 0.163], [Math.PI / 2, 0, 0]);
  const chestGlass = mesh('chest_glass', new THREE.CylinderGeometry(0.052, 0.052, 0.036, 32), M.lens, body, [0, 0.565, 0.169], [Math.PI / 2, 0, 0]);
  mesh('chest_glint', new THREE.SphereGeometry(0.011, 12, 10), M.white, body, [0.018, 0.583, 0.184]);
  // CamCom logo decal on the stomach (curved sticker matching the torso)
  const logoTex = new THREE.TextureLoader().load(logoUrl);
  logoTex.colorSpace = THREE.SRGBColorSpace;
  logoTex.repeat.set(1, 540 / 622); logoTex.offset.set(0, 82 / 622); // crop to the red square
  M.logo = new THREE.MeshStandardMaterial({ name: 'camcom_logo', map: logoTex, roughness: 0.4, metalness: 0.05 });
  mesh('stomach_logo', new THREE.CylinderGeometry(0.174, 0.174, 0.13, 24, 1, true, -0.375, 0.75), M.logo, body, [0, 0.4, 0]);

  // ---- arms ----
  const armGeo = new THREE.CapsuleGeometry(0.047, 0.13, 6, 20);
  const handGeo = new THREE.SphereGeometry(0.058, 24, 18);
  const shoulderL = new THREE.Group(); shoulderL.name = 'shoulder_L'; shoulderL.position.set(-0.205, 0.6, 0); body.add(shoulderL);
  const shoulderR = new THREE.Group(); shoulderR.name = 'shoulder_R'; shoulderR.position.set(0.205, 0.6, 0); body.add(shoulderR);
  mesh('arm_L', armGeo, M.black, shoulderL, [0, -0.115, 0]);
  mesh('arm_R', armGeo, M.black, shoulderR, [0, -0.115, 0]);
  mesh('hand_L', handGeo, M.red, shoulderL, [0, -0.235, 0]);
  mesh('hand_R', handGeo, M.red, shoulderR, [0, -0.235, 0]);

  // magnifying glass in left hand
  // magnifying glass held in left hand — pivots at the hand so tilting keeps it gripped
  const mag = new THREE.Group(); mag.name = 'magnifier'; mag.position.set(0, -0.235, 0); mag.rotation.x = -0.35; shoulderL.add(mag);
  mesh('mag_handle', new THREE.CylinderGeometry(0.014, 0.016, 0.11, 16), M.dark, mag, [0, -0.035, 0]);
  mesh('mag_collar', new THREE.SphereGeometry(0.018, 14, 12), M.red, mag, [0, -0.085, 0]);
  mesh('mag_ring', new THREE.TorusGeometry(0.062, 0.014, 14, 36), M.red, mag, [0, -0.155, 0], [Math.PI / 2, 0, 0]);
  mesh('mag_glass', new THREE.CylinderGeometry(0.052, 0.052, 0.007, 32), M.magGlass, mag, [0, -0.155, 0]);
  const beamGeo = new THREE.ConeGeometry(0.22, 0.85, 28, 1, true);
  beamGeo.translate(0, -0.425, 0); // apex at origin, extends -y
  const beam = mesh('scan_beam', beamGeo, M.beam, mag, [0, -0.165, 0]);
  beam.scale.setScalar(0.001); // kept tiny at rest so it doesn't inflate the auto-framed bounds

  // ---- head ----
  const head = new THREE.Group(); head.name = 'head'; head.position.set(0, 0.74, 0); body.add(head);
  mesh('neck', new THREE.CylinderGeometry(0.07, 0.08, 0.08, 20), M.dark, head, [0, -0.01, 0]);
  mesh('helmet', new THREE.SphereGeometry(0.215, 48, 36), M.black, head, [0, 0.175, 0], null, [1, 0.94, 0.98]);
  mesh('face', new THREE.SphereGeometry(0.19, 40, 30), M.white, head, [0, 0.17, 0.108], null, [0.9, 0.82, 0.6]);
  // visor band + ear pods
  mesh('visor_band', new THREE.TorusGeometry(0.2, 0.023, 16, 48), M.red, head, [0, 0.262, 0], [Math.PI / 2, 0, 0]);
  for (const s of [-1, 1]) {
    const side = s < 0 ? 'L' : 'R';
    mesh('earpod_' + side, new THREE.CylinderGeometry(0.052, 0.052, 0.05, 24), M.red, head, [s * 0.215, 0.175, 0], [0, 0, Math.PI / 2]);
    mesh('earcap_' + side, new THREE.CylinderGeometry(0.032, 0.032, 0.012, 20), M.dark, head, [s * 0.243, 0.175, 0], [0, 0, Math.PI / 2]);
  }
  // antenna
  const antenna = new THREE.Group(); antenna.name = 'antenna'; antenna.position.set(0, 0.36, 0); head.add(antenna);
  mesh('antenna_stem', new THREE.CylinderGeometry(0.011, 0.011, 0.1, 12), M.dark, antenna, [0, 0.05, 0]);
  mesh('antenna_tip', new THREE.SphereGeometry(0.027, 18, 14), M.red, antenna, [0, 0.108, 0]);

  // eyes
  const mkEye = (s) => {
    const g = new THREE.Group(); g.name = 'eye_' + (s < 0 ? 'L' : 'R');
    g.position.set(s * 0.068, 0.2, 0.208); head.add(g);
    mesh('pupil' + (s < 0 ? '_L' : '_R'), new THREE.SphereGeometry(0.05, 28, 22), M.dark, g, [0, 0, 0], null, [1, 1.18, 0.5]);
    mesh('iris' + (s < 0 ? '_L' : '_R'), new THREE.TorusGeometry(0.049, 0.0085, 12, 32), M.red, g, [0, 0, 0.008], null, [1, 1.18, 1]);
    mesh('catchlight' + (s < 0 ? '_L' : '_R'), new THREE.SphereGeometry(0.012, 12, 10), M.white, g, [0.016, 0.024, 0.022]);
    return g;
  };
  const eyeL = mkEye(-1), eyeR = mkEye(1);
  // smile + cheeks
  mesh('smile', new THREE.TorusGeometry(0.042, 0.008, 10, 28, 2.1), M.dark, head, [0, 0.118, 0.208], [0, 0, Math.PI + (Math.PI - 2.1) / 2]);
  mesh('cheek_L', new THREE.SphereGeometry(0.024, 14, 12), M.red, head, [-0.12, 0.142, 0.19], null, [1, 0.7, 0.5]);
  mesh('cheek_R', new THREE.SphereGeometry(0.024, 14, 12), M.red, head, [0.12, 0.142, 0.19], null, [1, 0.7, 0.5]);

  // rest poses
  shoulderL.rotation.z = -0.12; shoulderR.rotation.z = 0.12;

  // ---- animation ----
  const clamp = (x, a, b) => Math.max(a, Math.min(b, x));
  const ss = (a, b, x) => { const k = clamp((x - a) / (b - a), 0, 1); return k * k * (3 - 2 * k); };
  const pulse = (t, a, b, f) => ss(a, a + f, t) * (1 - ss(b - f, b, t));
  const CYCLE = 14;

  function tick(t) {
    const tc = t % CYCLE;
    const wWave = pulse(tc, 2, 5.2, 0.6);      // wave hello window
    const wScan = pulse(tc, 8, 12.2, 0.7);     // scan-beam window
    const idle = 1 - Math.max(wWave, wScan);

    // hover bob + squash
    const bobP = Math.sin(t * 4.2);
    body.position.y = 0.013 * bobP;
    body.scale.y = 1 + 0.008 * Math.sin(t * 4.2 + Math.PI);

    // head look-around / tilt, blended with wave & scan targets
    const sweep = Math.sin((tc - 8.4) * 1.9);
    head.rotation.y = idle * 0.26 * Math.sin(t * 0.47) + wWave * 0.3 + wScan * 0.22 * sweep;
    head.rotation.z = idle * 0.05 * Math.sin(t * 0.61) + wWave * 0.12;
    head.rotation.x = wScan * 0.14;

    // antenna wiggle
    antenna.rotation.z = 0.15 * Math.sin(t * 3.2) + 0.3 * wWave * Math.sin(t * 9);
    antenna.rotation.x = 0.08 * Math.sin(t * 2.1);

    // blink (both eyes), every ~3.1s
    const bp = t % 3.1;
    const blink = bp < 0.14 ? 0.1 + 0.9 * Math.abs(Math.cos((bp / 0.14) * Math.PI)) : 1;
    eyeL.scale.y = blink; eyeR.scale.y = blink;

    // right arm wave hello
    const wiggle = pulse(tc, 2.7, 4.6, 0.3) * 0.32 * Math.sin(t * 11);
    shoulderR.rotation.z = 0.12 + wWave * (2.35 + wiggle);
    shoulderR.rotation.x = wWave * -0.15;

    // left arm scan + beam sweep
    shoulderL.rotation.x = -0.95 * wScan;
    shoulderL.rotation.z = -0.12 + wScan * 0.3 * sweep;
    const wBeam = pulse(tc, 8.5, 11.8, 0.5);
    M.beam.opacity = 0.16 * wBeam;
    beam.scale.setScalar(Math.max(0.001, wBeam));
    M.lens.emissive.setHex(0x8c1116).multiplyScalar(wScan);
    chestGlass.material = M.lens;

    // gentle idle arm sway
    shoulderR.rotation.x += idle * 0.05 * Math.sin(t * 1.3);
    shoulderL.rotation.x += idle * 0.05 * Math.sin(t * 1.3 + Math.PI);
  }

  return { group: root, tick, rig: { body, head, antenna, eyeL, eyeR, shoulderL, shoulderR, mag, hipL: hips.L, hipR: hips.R, beamMat: M.beam, mats: M } };
}
